import React, { useLayoutEffect, useState } from 'react';
import axios, { AxiosInstance, AxiosResponse } from 'axios';
import { Loader } from '@/components';
import LocalStorage from '@/service/LocalStorage';
import ApiService from '@/service/Api/index';
import { REST_API_URI } from '@/index';
import { setAccessToken } from '@/utils/accessToken';
import { useNotification } from '@/context/NotificationContext';
import { logout } from '@/utils/logout';
import { silentFailURLs } from '@/utils/constants/silent';
import { extractErrorMessage } from '@/service/Api/errorHelper';

const AxiosProvider: React.FC<{ apiClient: AxiosInstance; children: React.ReactElement<any, any> }> = ({
  children,
  apiClient,
}) => {
  const [isSet, setIsSet] = useState(false);
  const { showNotification } = useNotification();

  useLayoutEffect(() => {
    const resInterceptor = (response: AxiosResponse) => {
      return response;
    };

    let timeout: NodeJS.Timeout;
    const errorInterceptor = async (error: any) => {
      if (error.response?.status === 401) {
        const errorType = error.response.data.error_type;

        // handles incorrect user / password credentials
        if (errorType === 'invalid_credentials') {
          showNotification({
            type: 'error',
            message: 'Invalid Credentials',
            description: `${error.response?.data.error || error.response?.data || error.message}`,
          });
          timeout = setTimeout(() => {
            logout();
          }, 3000);
          // handles expired access token
        } else if (errorType === 'token_expired') {
          try {
            const response = await axios.post(
              `${REST_API_URI}/services/auth/refresh_token`,
              {},
              {
                headers: {
                  'x-refresh-token': `${LocalStorage.get('refresh_token')}`,
                },
              }
            );

            if (response.data.authorized && response.data.accessToken) {
              apiClient.defaults.headers.common['Authorization'] = response.data.accessToken;
              setAccessToken(response.data.accessToken);
              ApiService.init(response.data.accessToken);
              const originalRequest = error.config;
              originalRequest.headers['Authorization'] = `Bearer ${response.data.accessToken}`;
              return await apiClient.request(originalRequest);
            } else {
              logout();
              return Promise.reject(error);
            }
          } catch (refreshError) {
            logout();
            return Promise.reject(refreshError);
          }
        } else {
          logout();
        }
      } else {
        if (
          error.code !== 'ERR_CANCELED' &&
          error.response?.status !== 409 &&
          !silentFailURLs.includes(error.response?.config.url)
        ) {
          showNotification({
            type: 'error',
            message: 'Rest API error',
            description: extractErrorMessage(error, error.message),
            skip: process.env.ARYA_PLATFORM === 'production',
          });
          console.error(error.response?.data || error.message);
        }
      }

      return Promise.reject(error);
    };

    setIsSet(true);
    const interceptor = apiClient.interceptors.response.use(resInterceptor, errorInterceptor);
    return () => {
      apiClient.interceptors.response.eject(interceptor);
      clearTimeout(timeout);
    };
  }, [apiClient.interceptors.response, apiClient, showNotification]);

  return isSet ? <>{children}</> : <Loader />;
};

export default AxiosProvider;
